import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  DEFAULT_CONSENT,
  acceptAllConsent,
  getStoredConsent,
  rejectNonEssentialConsent,
  saveConsent,
} from "./consentStorage";

const ConsentContext = createContext(null);

export const ConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(null);
  const [hydrated, setHydrated] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    setConsent(getStoredConsent());
    setHydrated(true);
  }, []);

  const hasChoice = consent !== null;

  const acceptAll = useCallback(() => {
    setConsent(acceptAllConsent());
    setSettingsOpen(false);
  }, []);

  const rejectNonEssential = useCallback(() => {
    setConsent(rejectNonEssentialConsent());
    setSettingsOpen(false);
  }, []);

  const updateConsent = useCallback((partial) => {
    setConsent((prev) =>
      saveConsent({
        analytics: partial.analytics ?? prev?.analytics ?? false,
        marketing: partial.marketing ?? prev?.marketing ?? false,
      })
    );
    setSettingsOpen(false);
  }, []);

  const openSettings = useCallback(() => setSettingsOpen(true), []);
  const closeSettings = useCallback(() => setSettingsOpen(false), []);

  const value = useMemo(
    () => ({
      consent: consent ?? DEFAULT_CONSENT,
      hasChoice,
      hydrated,
      settingsOpen,
      acceptAll,
      rejectNonEssential,
      updateConsent,
      openSettings,
      closeSettings,
    }),
    [
      consent,
      hasChoice,
      hydrated,
      settingsOpen,
      acceptAll,
      rejectNonEssential,
      updateConsent,
      openSettings,
      closeSettings,
    ]
  );

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
};

export const useConsent = () => {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error("useConsent must be used within ConsentProvider");
  }
  return ctx;
};
